import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Loader2, PlayCircle, Video, X } from "lucide-react";

import { PageHeader } from "@/components/layout";
import { StatusBadge } from "@/components/status-badge";
import { IconButton } from "@/components/ui/icon-button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

interface TrainingVideo {
  _id: Id<"trainingVideos">;
  title: string;
  description: string | null;
  durationSeconds: number | null;
  status: "ready" | "uploading" | "processing" | "failed";
  updatedAt: number;
}

function formatDuration(seconds: number | null) {
  if (!seconds || seconds <= 0) {
    return null;
  }
  const rounded = Math.round(seconds);
  const minutes = Math.floor(rounded / 60);
  const remainingSeconds = rounded % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

export const Route = createFileRoute("/training")({
  component: TrainingPage,
});

export function TrainingPage() {
  const { data: videos, isPending } = useQuery(
    convexQuery(api.trainingVideos.listPublished, {})
  );
  const [selectedVideo, setSelectedVideo] = useState<TrainingVideo | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const {
    data: playbackSession,
    isPending: isPlaybackPending,
    isError: isPlaybackError,
  } = useQuery(
    convexQuery(
      api.trainingVideos.getPlaybackSession,
      selectedVideo ? { videoId: selectedVideo._id } : "skip"
    )
  );

  const handlePlay = (video: TrainingVideo) => {
    setSelectedVideo(video);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedVideo(null);
  };

  const publishedVideos = (videos ?? []) as TrainingVideo[];

  return (
    <div className="space-y-4">
      <PageHeader title="Training Videos" />

      <Dialog open={dialogOpen} onOpenChange={(open) => {
        if (!open) handleCloseDialog();
      }}>
        <DialogContent
          className="!w-[94vw] !max-w-[94vw] max-h-[calc(100dvh-1.5rem)] gap-0 overflow-hidden p-0"
          showCloseButton={false}
        >
          <div className="relative">
            <div className="flex h-[62dvh] min-h-80 w-full items-center justify-center bg-black sm:h-[72dvh]">
              {isPlaybackError ? (
                <div className="px-4 text-center text-sm font-medium text-destructive">
                  Unable to load this video right now. Please try again.
                </div>
              ) : playbackSession ? (
                <video
                  key={playbackSession.videoUrl}
                  title={selectedVideo?.title}
                  src={playbackSession.videoUrl}
                  className="h-full w-full object-contain"
                  controls
                  playsInline
                  preload="metadata"
                  autoPlay
                />
              ) : isPlaybackPending ? (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  <Loader2 className="size-7 animate-spin" />
                </div>
              ) : (
                <div className="px-4 text-center text-sm font-medium text-muted-foreground">
                  This video is not available right now.
                </div>
              )}
            </div>
            <IconButton
              label="Close video"
              icon={X}
              variant="secondary"
              onClick={handleCloseDialog}
              className="absolute top-3 right-3 rounded-full opacity-80 hover:opacity-100 focus-visible:opacity-100"
              iconClassName="size-5"
            />
          </div>
          <DialogHeader className="border-t bg-background p-4 pb-3 sm:p-5">
            <DialogTitle className="text-lg">{selectedVideo?.title}</DialogTitle>
            {selectedVideo?.description ? (
              <DialogDescription>{selectedVideo.description}</DialogDescription>
            ) : null}
          </DialogHeader>
        </DialogContent>
      </Dialog>

      {isPending ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : publishedVideos.length === 0 ? (
        <Card className="py-10">
          <CardContent className="flex flex-col items-center text-center">
            <Video className="mb-3 size-10 text-muted-foreground" />
            <h2 className="text-lg font-bold tracking-tight">No training videos yet</h2>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              Published training videos will appear here after an admin uploads them.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {publishedVideos.map((video) => {
            const duration = formatDuration(video.durationSeconds);
            return (
              <Card key={video._id} className="overflow-hidden py-0">
                <button
                  type="button"
                  className="group relative aspect-video w-full bg-muted text-left"
                  onClick={() => handlePlay(video)}
                  aria-label={`Play ${video.title}`}
                >
                  <div className="flex h-full items-center justify-center bg-muted text-muted-foreground">
                    <Video className="size-10" />
                  </div>
                  <span className="absolute inset-0 flex items-center justify-center bg-black/15 opacity-100 transition group-hover:bg-black/25">
                    {selectedVideo?._id === video._id && isPlaybackPending && dialogOpen ? (
                      <Loader2 className="size-11 animate-spin text-white" />
                    ) : (
                      <PlayCircle className="size-12 text-white drop-shadow" />
                    )}
                  </span>
                  <StatusBadge
                    status="completed"
                    label="Ready"
                    className="absolute top-2 left-2"
                  />
                  {duration ? (
                    <span className="absolute right-2 bottom-2 rounded bg-black/75 px-2 py-1 text-xs font-bold text-white">
                      {duration}
                    </span>
                  ) : null}
                </button>
                <CardHeader className="pb-4">
                  <CardTitle className="line-clamp-2 text-base">{video.title}</CardTitle>
                  {video.description ? (
                    <p className="line-clamp-3 text-sm text-muted-foreground">{video.description}</p>
                  ) : null}
                </CardHeader>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
